import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall } from '../../config/api';
import moment from 'moment';

interface PatientFormData {
  // Personal Information
  title: string;
  surname: string;
  given_name: string;
  middle_name: string;
  preferred_name: string;
  maiden_name: string;
  gender: string;
  date_of_birth: string;
  place_of_birth: string;
  interpreter_required: boolean;
  language: string;
  marital_status: string;
  deceased: boolean;
  accuracy: string;

  // Entitlement
  medicare_number: string;
  medicare_reference: string;
  medicare_expiry: string;
  veteran_number: string;
  veteran_expiry: string;
  pension_hcc: string;
  pension_hcc_expiry: string;
  status: string;
  safety_net: string;
  atsi: string;
  medicare_plus_incentive: boolean;
  ctg_copayment_relief: boolean;

  // Health Fund
  fund_id: string;
  member_number: string;
  reference: string;
  alias_family: string;
  alias_first: string;

  // Residential Address
  residential_address: string;
  residential_address_2: string;
  residential_suburb: string;
  residential_postcode: string;
  residential_type: string;
  residential_community: string;
  send_to_medicare: boolean;

  // Postal Address
  postal_address: string;
  postal_address_2: string;
  postal_suburb: string;
  postal_postcode: string;

  // Contact Details
  home_phone: string;
  mobile_phone: string;
  accept_sms: boolean;
  email: string;
  accept_email: boolean;
  fax: string;
  note: string;

  // Warnings
  ask_consent_prescriptions: boolean;
  no_eprescribing: boolean;

  // Next of Kin
  next_of_kin_name: string;
  next_of_kin_relationship: string;
  next_of_kin_phone: string;

  // Emergency Contact
  emergency_contact_name: string;
  emergency_contact_relationship: string;
  emergency_contact_phone: string;
  emergency_same_as_nok: boolean;

  // Account
  account_type: string;
  fee_rate: string;
  payer: string;
  account_name: string;
  bsb: string;
  account_number: string;
  use_medicare_bank: boolean;

  // File
  file_number: string;
  ur_number: string;
  created_date: string;
  archived_date: string;
}

const NewPatient: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'residential' | 'postal' | 'other'>('residential');
  const [activeReferralTab, setActiveReferralTab] = useState<'referrals' | 'wc_tac' | 'family' | 'occupations' | 'ehealth' | 'udf'>('referrals');
  
  // GP Referrals
  const [isGpReferralModalOpen, setIsGpReferralModalOpen] = useState(false);
  const [gpReferrals, setGpReferrals] = useState<any[]>([]);
  const [currentGpReferral, setCurrentGpReferral] = useState({
    provider: '',
    gp: false,
    issue_date: '',
    period: '12 months',
    first_visit: '',
    type: 'General Refer',
    is_default: false,
    expired: false,
    note: ''
  });
  
  // WC/TAC Referrals
  const [isWcTacModalOpen, setIsWcTacModalOpen] = useState(false);
  const [wcTacReferrals, setWcTacReferrals] = useState<any[]>([]);
  const [currentWcTac, setCurrentWcTac] = useState({
    date_of_injury: '',
    claim_number: '',
    payer: '',
    employer: '',
    case_manager: '',
    is_default: false
  });

  const [formData, setFormData] = useState<PatientFormData>({
    title: '',
    surname: '',
    given_name: '',
    middle_name: '',
    preferred_name: '',
    maiden_name: '',
    gender: '',
    date_of_birth: '',
    place_of_birth: '',
    interpreter_required: false,
    language: '',
    marital_status: '',
    deceased: false,
    accuracy: '',
    medicare_number: '',
    medicare_reference: '',
    medicare_expiry: '',
    veteran_number: '',
    veteran_expiry: '',
    pension_hcc: '',
    pension_hcc_expiry: '',
    status: '',
    safety_net: '',
    atsi: 'Not Asked',
    medicare_plus_incentive: false,
    ctg_copayment_relief: false,
    fund_id: '',
    member_number: '',
    reference: '',
    alias_family: '',
    alias_first: '',
    residential_address: '',
    residential_address_2: '',
    residential_suburb: '',
    residential_postcode: '',
    residential_type: 'Not Specified',
    residential_community: '',
    send_to_medicare: false,
    postal_address: '',
    postal_address_2: '',
    postal_suburb: '',
    postal_postcode: '',
    home_phone: '',
    mobile_phone: '',
    accept_sms: false,
    email: '',
    accept_email: false,
    fax: '',
    note: '',
    ask_consent_prescriptions: false,
    no_eprescribing: false,
    next_of_kin_name: '',
    next_of_kin_relationship: '',
    next_of_kin_phone: '',
    emergency_contact_name: '',
    emergency_contact_relationship: '',
    emergency_contact_phone: '',
    emergency_same_as_nok: false,
    account_type: 'Not Specified',
    fee_rate: '',
    payer: '',
    account_name: '',
    bsb: '',
    account_number: '',
    use_medicare_bank: false,
    file_number: '00055',
    ur_number: '',
    created_date: moment().format('YYYY-MM-DD'),
    archived_date: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await apiCall('/patients/', {
        method: 'POST',
        body: JSON.stringify(formData)
      }, token || undefined);

      if (response.ok) {
        const newPatient = await response.json();
        navigate(`/patients/${newPatient.id}`);
      } else {
        console.error('Failed to create patient');
      }
    } catch (error) {
      console.error('Error creating patient:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-6 px-4">
      {/* Header */}
      <div className="card bg-gradient-to-r from-primary to-secondary text-primary-content shadow-xl mb-6">
        <div className="card-body py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold">New Patient Registration</h1>
            <div className="flex gap-2">
              <button
                onClick={() => navigate('/patients')}
                className="btn btn-sm btn-outline text-primary-content border-primary-content hover:bg-primary-content hover:text-primary"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="btn btn-sm bg-base-100 text-base-content hover:bg-base-200"
              >
                {loading ? <span className="loading loading-spinner loading-sm"></span> : 'Save Patient'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Personal Information */}
            <div className="card bg-base-100 shadow-xl border border-accent">
              <div className="card-body">
                <h3 className="text-lg font-semibold mb-4">Personal</h3>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">Surname</span>
                    </label>
                    <input
                      type="text"
                      name="surname"
                      value={formData.surname}
                      onChange={handleInputChange}
                      className="input input-bordered"
                      required
                    />
                  </div>
                  
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">Given</span>
                    </label>
                    <input
                      type="text"
                      name="given_name"
                      value={formData.given_name}
                      onChange={handleInputChange}
                      className="input input-bordered"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">Middle</span>
                    </label>
                    <input
                      type="text"
                      name="middle_name"
                      value={formData.middle_name}
                      onChange={handleInputChange}
                      className="input input-bordered"
                    />
                  </div>
                  
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">Preferred</span>
                    </label>
                    <input
                      type="text"
                      name="preferred_name"
                      value={formData.preferred_name}
                      onChange={handleInputChange}
                      className="input input-bordered"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">Maiden</span>
                    </label>
                    <input
                      type="text"
                      name="maiden_name"
                      value={formData.maiden_name}
                      onChange={handleInputChange}
                      className="input input-bordered"
                    />
                  </div>
                  
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs flex items-center gap-1">
                        <input
                          type="checkbox"
                          name="interpreter_required"
                          checked={formData.interpreter_required}
                          onChange={handleInputChange}
                          className="checkbox checkbox-xs checkbox-primary"
                        />
                        Interpreter? Language
                      </span>
                    </label>
                    <select
                      name="language"
                      value={formData.language}
                      onChange={handleInputChange}
                      className="select select-bordered"
                    >
                      <option value="">Select...</option>
                      <option value="English">English</option>
                      <option value="Mandarin">Mandarin</option>
                      <option value="Arabic">Arabic</option>
                      <option value="Cantonese">Cantonese</option>
                      <option value="Vietnamese">Vietnamese</option>
                      <option value="Italian">Italian</option>
                      <option value="Greek">Greek</option>
                      <option value="Hindi">Hindi</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">Title</span>
                    </label>
                    <select
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="select select-bordered"
                    >
                      <option value="">Select...</option>
                      <option value="Mr">Mr</option>
                      <option value="Mrs">Mrs</option>
                      <option value="Ms">Ms</option>
                      <option value="Miss">Miss</option>
                      <option value="Dr">Dr</option>
                      <option value="Prof">Prof</option>
                    </select>
                  </div>
                  
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">Gender</span>
                    </label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      className="select select-bordered"
                      required
                    >
                      <option value="">Select...</option>
                      <option value="M">Male</option>
                      <option value="F">Female</option>
                      <option value="O">Other</option>
                      <option value="U">Unknown</option>
                    </select>
                  </div>
                  
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">D.O.B</span>
                    </label>
                    <input
                      type="date"
                      name="date_of_birth"
                      value={formData.date_of_birth}
                      onChange={handleInputChange}
                      className="input input-bordered"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">P.O.B</span>
                    </label>
                    <select
                      name="place_of_birth"
                      value={formData.place_of_birth}
                      onChange={handleInputChange}
                      className="select select-bordered"
                    >
                      <option value="">Select...</option>
                      <option value="Australia">Australia</option>
                      <option value="United Kingdom">United Kingdom</option>
                      <option value="New Zealand">New Zealand</option>
                      <option value="China">China</option>
                      <option value="India">India</option>
                    </select>
                  </div>
                  
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">Marital Status</span>
                    </label>
                    <select
                      name="marital_status"
                      value={formData.marital_status}
                      onChange={handleInputChange}
                      className="select select-bordered"
                    >
                      <option value="">Select...</option>
                      <option value="Single">Single</option>
                      <option value="Married">Married</option>
                      <option value="Divorced">Divorced</option>
                      <option value="Widowed">Widowed</option>
                      <option value="De Facto">De Facto</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">Deceased</span>
                    </label>
                    <select
                      name="deceased"
                      value={formData.deceased ? 'true' : 'false'}
                      onChange={handleInputChange}
                      className="select select-bordered"
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                  </div>
                  
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">Accuracy</span>
                    </label>
                    <select
                      name="accuracy"
                      value={formData.accuracy}
                      onChange={handleInputChange}
                      className="select select-bordered"
                    >
                      <option value="">Select...</option>
                      <option value="Verified">Verified</option>
                      <option value="Unverified">Unverified</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Entitlement */}
            <div className="card bg-base-100 shadow-xl border border-accent">
              <div className="card-body">
                <h3 className="text-lg font-semibold mb-4">Entitlement</h3>
                
                <div className="grid grid-cols-3 gap-2">
                  <div className="form-control col-span-2">
                    <label className="label py-1">
                      <span className="label-text text-xs">Medicare#</span>
                    </label>
                    <input
                      type="text"
                      name="medicare_number"
                      value={formData.medicare_number}
                      onChange={handleInputChange}
                      placeholder="123456789"
                      className="input input-bordered"
                      maxLength={10}
                    />
                  </div>
                  
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">Ref</span>
                    </label>
                    <input
                      type="text"
                      name="medicare_reference"
                      value={formData.medicare_reference}
                      onChange={handleInputChange}
                      placeholder="1"
                      className="input input-bordered"
                      maxLength={1}
                    />
                  </div>
                </div>

                <div className="form-control">
                  <label className="label py-1">
                    <span className="label-text text-xs">EXP</span>
                  </label>
                  <input
                    type="month"
                    name="medicare_expiry"
                    value={formData.medicare_expiry}
                    onChange={handleInputChange}
                    className="input input-bordered"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">Veteran#</span>
                    </label>
                    <input
                      type="text"
                      name="veteran_number"
                      value={formData.veteran_number}
                      onChange={handleInputChange}
                      className="input input-bordered"
                    />
                  </div>
                  
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">EXP</span>
                    </label>
                    <input
                      type="month"
                      name="veteran_expiry"
                      value={formData.veteran_expiry}
                      onChange={handleInputChange}
                      className="input input-bordered"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">Pension/HCC</span>
                    </label>
                    <input
                      type="text"
                      name="pension_hcc"
                      value={formData.pension_hcc}
                      onChange={handleInputChange}
                      className="input input-bordered"
                    />
                  </div>
                  
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">EXP</span>
                    </label>
                    <input
                      type="month"
                      name="pension_hcc_expiry"
                      value={formData.pension_hcc_expiry}
                      onChange={handleInputChange}
                      className="input input-bordered"
                    />
                  </div>
                </div>

                <div className="form-control">
                  <label className="label py-1">
                    <span className="label-text text-xs">Status</span>
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="select select-bordered"
                  >
                    <option value="">Select...</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">Safety Net</span>
                    </label>
                    <input
                      type="text"
                      name="safety_net"
                      value={formData.safety_net}
                      onChange={handleInputChange}
                      className="input input-bordered"
                    />
                  </div>
                  
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">ATSI</span>
                    </label>
                    <select
                      name="atsi"
                      value={formData.atsi}
                      onChange={handleInputChange}
                      className="select select-bordered"
                    >
                      <option value="Not Asked">Not Asked</option>
                      <option value="Aboriginal">Aboriginal</option>
                      <option value="Torres Strait Islander">Torres Strait Islander</option>
                      <option value="Both">Both Aboriginal and TSI</option>
                      <option value="Neither">Neither</option>
                    </select>
                  </div>
                </div>

                <div className="form-control">
                  <label className="label cursor-pointer justify-start gap-2 py-1">
                    <input
                      type="checkbox"
                      name="medicare_plus_incentive"
                      checked={formData.medicare_plus_incentive}
                      onChange={handleInputChange}
                      className="checkbox checkbox-sm checkbox-primary"
                    />
                    <span className="label-text text-xs">Medicare+ Incentive</span>
                  </label>
                </div>

                <div className="form-control">
                  <label className="label cursor-pointer justify-start gap-2 py-1">
                    <input
                      type="checkbox"
                      name="ctg_copayment_relief"
                      checked={formData.ctg_copayment_relief}
                      onChange={handleInputChange}
                      className="checkbox checkbox-sm checkbox-primary"
                    />
                    <span className="label-text text-xs">CTG CoPayment Relief</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Health Fund */}
            <div className="card bg-base-100 shadow-xl border border-accent">
              <div className="card-body">
                <h3 className="text-lg font-semibold mb-4">Health Fund</h3>
                
                <div className="form-control">
                  <label className="label py-1">
                    <span className="label-text text-xs">Fund Id</span>
                  </label>
                  <select
                    name="fund_id"
                    value={formData.fund_id}
                    onChange={handleInputChange}
                    className="select select-bordered"
                  >
                    <option value="">Select...</option>
                    <option value="Medibank">Medibank</option>
                    <option value="Bupa">Bupa</option>
                    <option value="HCF">HCF</option>
                    <option value="NIB">NIB</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">Member#</span>
                    </label>
                    <input
                      type="text"
                      name="member_number"
                      value={formData.member_number}
                      onChange={handleInputChange}
                      className="input input-bordered"
                    />
                  </div>
                  
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">Ref</span>
                    </label>
                    <input
                      type="text"
                      name="reference"
                      value={formData.reference}
                      onChange={handleInputChange}
                      className="input input-bordered"
                    />
                  </div>
                </div>

                <div className="form-control">
                  <label className="label py-1">
                    <span className="label-text text-xs">Alias Family</span>
                  </label>
                  <input
                    type="text"
                    name="alias_family"
                    value={formData.alias_family}
                    onChange={handleInputChange}
                    className="input input-bordered"
                  />
                </div>

                <div className="form-control">
                  <label className="label py-1">
                    <span className="label-text text-xs">Alias First</span>
                  </label>
                  <input
                    type="text"
                    name="alias_first"
                    value={formData.alias_first}
                    onChange={handleInputChange}
                    className="input input-bordered"
                  />
                </div>
              </div>
            </div>

            {/* File */}
            <div className="card bg-base-100 shadow-xl border border-accent">
              <div className="card-body">
                <h3 className="text-lg font-semibold mb-4">File</h3>
                
                <div className="grid grid-cols-2 gap-2">
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">File#</span>
                    </label>
                    <input
                      type="text"
                      name="file_number"
                      value={formData.file_number}
                      onChange={handleInputChange}
                      className="input input-bordered"
                    />
                  </div>
                  
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">UR No</span>
                    </label>
                    <input
                      type="text"
                      name="ur_number"
                      value={formData.ur_number}
                      onChange={handleInputChange}
                      className="input input-bordered"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">Created</span>
                    </label>
                    <input
                      type="date"
                      name="created_date"
                      value={formData.created_date}
                      onChange={handleInputChange}
                      className="input input-bordered"
                    />
                  </div>
                  
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">Archived</span>
                    </label>
                    <input
                      type="date"
                      name="archived_date"
                      value={formData.archived_date}
                      onChange={handleInputChange}
                      className="input input-bordered"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Middle Column */}
          <div className="space-y-6">
            {/* Address Section with Tabs */}
            <div className="card bg-base-100 shadow-xl border border-accent">
              <div className="card-body">
                <div className="tabs tabs-boxed mb-4">
                  <a
                    className={`tab ${activeTab === 'residential' ? 'tab-active' : ''}`}
                    onClick={() => setActiveTab('residential')}
                  >
                    Residential
                  </a>
                  <a
                    className={`tab ${activeTab === 'postal' ? 'tab-active' : ''}`}
                    onClick={() => setActiveTab('postal')}
                  >
                    Postal
                  </a>
                  <a
                    className={`tab ${activeTab === 'other' ? 'tab-active' : ''}`}
                    onClick={() => setActiveTab('other')}
                  >
                    Other Addresses
                  </a>
                </div>

                {activeTab === 'residential' && (
                  <div className="space-y-3">
                    <div className="form-control">
                      <label className="label py-1">
                        <span className="label-text text-xs">Address</span>
                      </label>
                      <input
                        type="text"
                        name="residential_address"
                        value={formData.residential_address}
                        onChange={handleInputChange}
                        className="input input-bordered"
                      />
                    </div>

                    <div className="form-control">
                      <input
                        type="text"
                        name="residential_address_2"
                        value={formData.residential_address_2}
                        onChange={handleInputChange}
                        className="input input-bordered"
                        placeholder="Address line 2"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="form-control">
                        <label className="label py-1">
                          <span className="label-text text-xs">Suburb</span>
                        </label>
                        <input
                          type="text"
                          name="residential_suburb"
                          value={formData.residential_suburb}
                          onChange={handleInputChange}
                          className="input input-bordered"
                        />
                      </div>
                      
                      <div className="form-control">
                        <label className="label py-1">
                          <span className="label-text text-xs">Postcode</span>
                        </label>
                        <input
                          type="text"
                          name="residential_postcode"
                          value={formData.residential_postcode}
                          onChange={handleInputChange}
                          className="input input-bordered"
                          maxLength={4}
                        />
                      </div>
                    </div>

                    <div className="form-control">
                      <label className="label py-1">
                        <span className="label-text text-xs">Type</span>
                      </label>
                      <select
                        name="residential_type"
                        value={formData.residential_type}
                        onChange={handleInputChange}
                        className="select select-bordered"
                      >
                        <option value="Not Specified">Not Specified</option>
                        <option value="House">House</option>
                        <option value="Unit">Unit</option>
                        <option value="Apartment">Apartment</option>
                        <option value="Townhouse">Townhouse</option>
                      </select>
                    </div>

                    <div className="form-control">
                      <label className="label py-1">
                        <span className="label-text text-xs">Community</span>
                      </label>
                      <input
                        type="text"
                        name="residential_community"
                        value={formData.residential_community}
                        onChange={handleInputChange}
                        className="input input-bordered"
                      />
                    </div>

                    <div className="form-control">
                      <label className="label cursor-pointer justify-start gap-2 py-1">
                        <input
                          type="checkbox"
                          name="send_to_medicare"
                          checked={formData.send_to_medicare}
                          onChange={handleInputChange}
                          className="checkbox checkbox-sm checkbox-primary"
                        />
                        <span className="label-text text-xs">Send To Medicare</span>
                      </label>
                    </div>
                  </div>
                )}

                {activeTab === 'postal' && (
                  <div className="space-y-3">
                    <div className="form-control">
                      <label className="label py-1">
                        <span className="label-text text-xs">Address</span>
                      </label>
                      <input
                        type="text"
                        name="postal_address"
                        value={formData.postal_address}
                        onChange={handleInputChange}
                        className="input input-bordered"
                      />
                    </div>

                    <div className="form-control">
                      <input
                        type="text"
                        name="postal_address_2"
                        value={formData.postal_address_2}
                        onChange={handleInputChange}
                        className="input input-bordered"
                        placeholder="Address line 2"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="form-control">
                        <label className="label py-1">
                          <span className="label-text text-xs">Suburb</span>
                        </label>
                        <input
                          type="text"
                          name="postal_suburb"
                          value={formData.postal_suburb}
                          onChange={handleInputChange}
                          className="input input-bordered"
                        />
                      </div>
                      
                      <div className="form-control">
                        <label className="label py-1">
                          <span className="label-text text-xs">Postcode</span>
                        </label>
                        <input
                          type="text"
                          name="postal_postcode"
                          value={formData.postal_postcode}
                          onChange={handleInputChange}
                          className="input input-bordered"
                          maxLength={4}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'other' && (
                  <div className="text-center py-8 text-base-content opacity-60">
                    <p className="text-sm">Additional addresses can be added after patient creation</p>
                  </div>
                )}
              </div>
            </div>

            {/* Account */}
            <div className="card bg-base-100 shadow-xl border border-accent">
              <div className="card-body">
                <h3 className="text-lg font-semibold mb-4">Account</h3>
                
                <div className="grid grid-cols-2 gap-2">
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">Acc. Type</span>
                    </label>
                    <select
                      name="account_type"
                      value={formData.account_type}
                      onChange={handleInputChange}
                      className="select select-bordered"
                    >
                      <option value="Not Specified">Not Specified</option>
                      <option value="Standard">Standard</option>
                      <option value="Bulk Bill">Bulk Bill</option>
                      <option value="Private">Private</option>
                    </select>
                  </div>
                  
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">Fee Rate</span>
                    </label>
                    <select
                      name="fee_rate"
                      value={formData.fee_rate}
                      onChange={handleInputChange}
                      className="select select-bordered"
                    >
                      <option value="">Select...</option>
                      <option value="Standard">Standard</option>
                      <option value="Concession">Concession</option>
                      <option value="Pension">Pension</option>
                    </select>
                  </div>
                </div>

                <div className="form-control">
                  <label className="label py-1">
                    <span className="label-text text-xs">Payer</span>
                  </label>
                  <input
                    type="text"
                    name="payer"
                    value={formData.payer}
                    onChange={handleInputChange}
                    className="input input-bordered"
                  />
                </div>

                <div className="form-control">
                  <label className="label py-1">
                    <span className="label-text text-xs">Acc. Name</span>
                  </label>
                  <input
                    type="text"
                    name="account_name"
                    value={formData.account_name}
                    onChange={handleInputChange}
                    className="input input-bordered"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">BSB</span>
                    </label>
                    <input
                      type="text"
                      name="bsb"
                      value={formData.bsb}
                      onChange={handleInputChange}
                      className="input input-bordered"
                      maxLength={6}
                    />
                  </div>
                  
                  <div className="form-control">
                    <label className="label py-1">
                      <span className="label-text text-xs">Acc#</span>
                    </label>
                    <input
                      type="text"
                      name="account_number"
                      value={formData.account_number}
                      onChange={handleInputChange}
                      className="input input-bordered"
                    />
                  </div>
                </div>

                <div className="form-control">
                  <label className="label cursor-pointer justify-start gap-2 py-1">
                    <input
                      type="checkbox"
                      name="use_medicare_bank"
                      checked={formData.use_medicare_bank}
                      onChange={handleInputChange}
                      className="checkbox checkbox-sm checkbox-primary"
                    />
                    <span className="label-text text-xs">Use bank account registered with Medicare</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Referrals Tabs */}
            <div className="card bg-base-100 shadow-xl border border-accent">
              <div className="card-body">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Referrals</h3>
                  {activeReferralTab === 'referrals' && (
                    <button
                      type="button"
                      onClick={() => setIsGpReferralModalOpen(true)}
                      className="btn btn-sm btn-primary"
                    >
                      New
                    </button>
                  )}
                  {activeReferralTab === 'wc_tac' && (
                    <button
                      type="button"
                      onClick={() => setIsWcTacModalOpen(true)}
                      className="btn btn-sm btn-primary"
                    >
                      New
                    </button>
                  )}
                </div>
                
                <div className="tabs tabs-boxed tabs-xs mb-4">
                  <a
                    className={`tab tab-xs ${activeReferralTab === 'referrals' ? 'tab-active' : ''}`}
                    onClick={() => setActiveReferralTab('referrals')}
                  >
                    Referrals
                  </a>
                  <a
                    className={`tab tab-xs ${activeReferralTab === 'wc_tac' ? 'tab-active' : ''}`}
                    onClick={() => setActiveReferralTab('wc_tac')}
                  >
                    WC/TAC
                  </a>
                  <a
                    className={`tab tab-xs ${activeReferralTab === 'family' ? 'tab-active' : ''}`}
                    onClick={() => setActiveReferralTab('family')}
                  >
                    Family
                  </a>
                  <a
                    className={`tab tab-xs ${activeReferralTab === 'occupations' ? 'tab-active' : ''}`}
                    onClick={() => setActiveReferralTab('occupations')}
                  >
                    Occupations
                  </a>
                  <a
                    className={`tab tab-xs ${activeReferralTab === 'ehealth' ? 'tab-active' : ''}`}
                    onClick={() => setActiveReferralTab('ehealth')}
                  >
                    eHealth
                  </a>
                  <a
                    className={`tab tab-xs ${activeReferralTab === 'udf' ? 'tab-active' : ''}`}
                    onClick={() => setActiveReferralTab('udf')}
                  >
                    UDF
                  </a>
                </div>

                {activeReferralTab === 'referrals' && (
                  <div className="overflow-x-auto">
                    {gpReferrals.length > 0 ? (
                      <table className="table table-zebra">
                        <thead>
                          <tr>
                            <th></th>
                            <th>Provider</th>
                            <th>GP</th>
                            <th>Issue Date</th>
                            <th>Period</th>
                            <th>1st Visit</th>
                            <th>Type</th>
                            <th>Default</th>
                            <th>Expired</th>
                            <th>Note</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {gpReferrals.map((ref, index) => (
                            <tr key={index}>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-ghost btn-xs"
                                >
                                  ▶
                                </button>
                              </td>
                              <td>{ref.provider}</td>
                              <td>
                                {ref.gp && (
                                  <input type="checkbox" checked readOnly className="checkbox checkbox-sm" />
                                )}
                              </td>
                              <td>{ref.issue_date}</td>
                              <td>{ref.period}</td>
                              <td>{ref.first_visit}</td>
                              <td>{ref.type}</td>
                              <td>
                                {ref.is_default && (
                                  <input type="checkbox" checked readOnly className="checkbox checkbox-sm" />
                                )}
                              </td>
                              <td>
                                {ref.expired && (
                                  <input type="checkbox" checked readOnly className="checkbox checkbox-sm" />
                                )}
                              </td>
                              <td>{ref.note}</td>
                              <td>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setGpReferrals(gpReferrals.filter((_, i) => i !== index));
                                  }}
                                  className="btn btn-ghost btn-xs text-error"
                                >
                                  ×
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="text-center py-8 text-base-content opacity-60">
                        <p className="text-sm">No referrals added yet</p>
                      </div>
                    )}
                  </div>
                )}

                {activeReferralTab === 'wc_tac' && (
                  <div className="overflow-x-auto">
                    {wcTacReferrals.length > 0 ? (
                      <table className="table table-zebra">
                        <thead>
                          <tr>
                            <th></th>
                            <th>Date of Injury</th>
                            <th>Claim#</th>
                            <th>Payer</th>
                            <th>Employer</th>
                            <th>Case Manager</th>
                            <th>Default</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {wcTacReferrals.map((ref, index) => (
                            <tr key={index}>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-ghost btn-xs"
                                >
                                  ▶
                                </button>
                              </td>
                              <td>{ref.date_of_injury}</td>
                              <td>{ref.claim_number}</td>
                              <td>{ref.payer}</td>
                              <td>{ref.employer}</td>
                              <td>{ref.case_manager}</td>
                              <td>
                                {ref.is_default && (
                                  <input type="checkbox" checked readOnly className="checkbox checkbox-sm" />
                                )}
                              </td>
                              <td>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setWcTacReferrals(wcTacReferrals.filter((_, i) => i !== index));
                                  }}
                                  className="btn btn-ghost btn-xs text-error"
                                >
                                  ×
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="text-center py-8 text-base-content opacity-60">
                        <p className="text-sm">No WC/TAC referrals added yet</p>
                      </div>
                    )}
                  </div>
                )}

                {activeReferralTab !== 'referrals' && activeReferralTab !== 'wc_tac' && (
                  <div className="text-center py-8 text-base-content opacity-60">
                    <p className="text-sm">Referral details can be added after patient creation</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Contact Details */}
            <div className="card bg-base-100 shadow-xl border border-accent">
              <div className="card-body">
                <h3 className="text-lg font-semibold mb-4">Contact Details</h3>
                
                <div className="form-control">
                  <label className="label py-1">
                    <span className="label-text text-xs">Home Phone</span>
                  </label>
                  <input
                    type="tel"
                    name="home_phone"
                    value={formData.home_phone}
                    onChange={handleInputChange}
                    className="input input-bordered"
                    placeholder="(03) 9xxx xxxx"
                  />
                </div>

                <div className="form-control">
                  <label className="label py-1">
                    <span className="label-text text-xs">Mobile Phone</span>
                  </label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="tel"
                      name="mobile_phone"
                      value={formData.mobile_phone}
                      onChange={handleInputChange}
                      className="input input-bordered flex-1"
                      placeholder="04xx xxx xxx"
                    />
                    <label className="label cursor-pointer gap-1">
                      <input
                        type="checkbox"
                        name="accept_sms"
                        checked={formData.accept_sms}
                        onChange={handleInputChange}
                        className="checkbox checkbox-sm checkbox-primary"
                      />
                      <span className="label-text text-xs">Accept SMS</span>
                    </label>
                  </div>
                </div>

                <div className="form-control">
                  <label className="label py-1">
                    <span className="label-text text-xs">Email</span>
                  </label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="input input-bordered flex-1"
                    />
                    <label className="label cursor-pointer gap-1">
                      <input
                        type="checkbox"
                        name="accept_email"
                        checked={formData.accept_email}
                        onChange={handleInputChange}
                        className="checkbox checkbox-sm checkbox-primary"
                      />
                      <span className="label-text text-xs">Accept Email</span>
                    </label>
                  </div>
                </div>

                <div className="form-control">
                  <label className="label py-1">
                    <span className="label-text text-xs">Fax</span>
                  </label>
                  <input
                    type="tel"
                    name="fax"
                    value={formData.fax}
                    onChange={handleInputChange}
                    className="input input-bordered"
                  />
                </div>

                <div className="form-control">
                  <label className="label py-1">
                    <span className="label-text text-xs">Note</span>
                  </label>
                  <textarea
                    name="note"
                    value={formData.note}
                    onChange={handleInputChange}
                    className="textarea textarea-bordered h-20"
                  />
                </div>
              </div>
            </div>

            {/* Warnings */}
            <div className="card bg-base-100 shadow-xl border border-warning">
              <div className="card-body">
                <h3 className="text-lg font-semibold mb-4 text-warning">Warnings</h3>
                
                <div className="form-control">
                  <label className="label cursor-pointer justify-start gap-2 py-1">
                    <input
                      type="checkbox"
                      name="ask_consent_prescriptions"
                      checked={formData.ask_consent_prescriptions}
                      onChange={handleInputChange}
                      className="checkbox checkbox-sm checkbox-warning"
                    />
                    <span className="label-text text-xs">Ask for consent for every Prescription</span>
                  </label>
                </div>

                <div className="form-control">
                  <label className="label cursor-pointer justify-start gap-2 py-1">
                    <input
                      type="checkbox"
                      name="no_eprescribing"
                      checked={formData.no_eprescribing}
                      onChange={handleInputChange}
                      className="checkbox checkbox-sm checkbox-warning"
                    />
                    <span className="label-text text-xs">No ePrescribing</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Next of Kin */}
            <div className="card bg-base-100 shadow-xl border border-accent">
              <div className="card-body">
                <h3 className="text-lg font-semibold mb-4">Next of Kin</h3>
                
                <div className="form-control">
                  <label className="label py-1">
                    <span className="label-text text-xs">Name</span>
                  </label>
                  <input
                    type="text"
                    name="next_of_kin_name"
                    value={formData.next_of_kin_name}
                    onChange={handleInputChange}
                    className="input input-bordered"
                  />
                </div>

                <div className="form-control">
                  <label className="label py-1">
                    <span className="label-text text-xs">Relationship</span>
                  </label>
                  <select
                    name="next_of_kin_relationship"
                    value={formData.next_of_kin_relationship}
                    onChange={handleInputChange}
                    className="select select-bordered"
                  >
                    <option value="">Select...</option>
                    <option value="Spouse">Spouse</option>
                    <option value="Partner">Partner</option>
                    <option value="Parent">Parent</option>
                    <option value="Child">Child</option>
                    <option value="Sibling">Sibling</option>
                    <option value="Friend">Friend</option>
                  </select>
                </div>

                <div className="form-control">
                  <label className="label py-1">
                    <span className="label-text text-xs">Phone</span>
                  </label>
                  <input
                    type="tel"
                    name="next_of_kin_phone"
                    value={formData.next_of_kin_phone}
                    onChange={handleInputChange}
                    className="input input-bordered"
                  />
                </div>
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="card bg-base-100 shadow-xl border border-accent">
              <div className="card-body">
                <h3 className="text-lg font-semibold mb-4">Emergency</h3>
                
                <div className="form-control">
                  <label className="label py-1">
                    <span className="label-text text-xs">Name</span>
                  </label>
                  <input
                    type="text"
                    name="emergency_contact_name"
                    value={formData.emergency_contact_name}
                    onChange={handleInputChange}
                    className="input input-bordered"
                    disabled={formData.emergency_same_as_nok}
                  />
                </div>

                <div className="form-control">
                  <label className="label py-1">
                    <span className="label-text text-xs">Relationship</span>
                  </label>
                  <select
                    name="emergency_contact_relationship"
                    value={formData.emergency_contact_relationship}
                    onChange={handleInputChange}
                    className="select select-bordered"
                    disabled={formData.emergency_same_as_nok}
                  >
                    <option value="">Select...</option>
                    <option value="Spouse">Spouse</option>
                    <option value="Partner">Partner</option>
                    <option value="Parent">Parent</option>
                    <option value="Child">Child</option>
                    <option value="Sibling">Sibling</option>
                    <option value="Friend">Friend</option>
                  </select>
                </div>

                <div className="form-control">
                  <label className="label py-1">
                    <span className="label-text text-xs">Phone</span>
                  </label>
                  <input
                    type="tel"
                    name="emergency_contact_phone"
                    value={formData.emergency_contact_phone}
                    onChange={handleInputChange}
                    className="input input-bordered"
                    disabled={formData.emergency_same_as_nok}
                  />
                </div>

                <div className="form-control">
                  <label className="label cursor-pointer justify-start gap-2 py-1">
                    <input
                      type="checkbox"
                      name="emergency_same_as_nok"
                      checked={formData.emergency_same_as_nok}
                      onChange={handleInputChange}
                      className="checkbox checkbox-sm checkbox-primary"
                    />
                    <span className="label-text text-xs">Same as Next of Kin</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Action Bar */}
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-base-300">
          <button
            type="button"
            onClick={() => navigate('/patients')}
            className="btn btn-ghost"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary"
          >
            {loading ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                Saving...
              </>
            ) : (
              'Save Patient'
            )}
          </button>
        </div>
      </form>

      {/* GP Referral Modal */}
      {isGpReferralModalOpen && (
        <div className="modal modal-open">
          <div className="modal-box max-w-2xl">
            <h3 className="font-bold text-lg mb-4">Add Referral</h3>
            
            <div className="space-y-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Provider</span>
                </label>
                <input
                  type="text"
                  value={currentGpReferral.provider}
                  onChange={(e) => setCurrentGpReferral({...currentGpReferral, provider: e.target.value})}
                  className="input input-bordered"
                  placeholder="Dr. Smith"
                />
              </div>

              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-2">
                  <input
                    type="checkbox"
                    checked={currentGpReferral.gp}
                    onChange={(e) => setCurrentGpReferral({...currentGpReferral, gp: e.target.checked})}
                    className="checkbox checkbox-primary"
                  />
                  <span className="label-text">GP</span>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Issue Date</span>
                  </label>
                  <input
                    type="date"
                    value={currentGpReferral.issue_date}
                    onChange={(e) => setCurrentGpReferral({...currentGpReferral, issue_date: e.target.value})}
                    className="input input-bordered"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Period</span>
                  </label>
                  <select
                    value={currentGpReferral.period}
                    onChange={(e) => setCurrentGpReferral({...currentGpReferral, period: e.target.value})}
                    className="select select-bordered"
                  >
                    <option value="3 months">3 months</option>
                    <option value="6 months">6 months</option>
                    <option value="12 months">12 months</option>
                    <option value="Indefinite">Indefinite</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">1st Visit</span>
                  </label>
                  <input
                    type="date"
                    value={currentGpReferral.first_visit}
                    onChange={(e) => setCurrentGpReferral({...currentGpReferral, first_visit: e.target.value})}
                    className="input input-bordered"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Type</span>
                  </label>
                  <select
                    value={currentGpReferral.type}
                    onChange={(e) => setCurrentGpReferral({...currentGpReferral, type: e.target.value})}
                    className="select select-bordered"
                  >
                    <option value="General Refer">General Refer</option>
                    <option value="Specialist Referral">Specialist Referral</option>
                    <option value="Imaging Referral">Imaging Referral</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="form-control">
                  <label className="label cursor-pointer justify-start gap-2">
                    <input
                      type="checkbox"
                      checked={currentGpReferral.is_default}
                      onChange={(e) => setCurrentGpReferral({...currentGpReferral, is_default: e.target.checked})}
                      className="checkbox checkbox-primary"
                    />
                    <span className="label-text">Default</span>
                  </label>
                </div>

                <div className="form-control">
                  <label className="label cursor-pointer justify-start gap-2">
                    <input
                      type="checkbox"
                      checked={currentGpReferral.expired}
                      onChange={(e) => setCurrentGpReferral({...currentGpReferral, expired: e.target.checked})}
                      className="checkbox checkbox-warning"
                    />
                    <span className="label-text">Expired</span>
                  </label>
                </div>
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Note</span>
                </label>
                <textarea
                  value={currentGpReferral.note}
                  onChange={(e) => setCurrentGpReferral({...currentGpReferral, note: e.target.value})}
                  className="textarea textarea-bordered h-20"
                  placeholder="Additional notes..."
                />
              </div>
            </div>

            <div className="modal-action">
              <button
                type="button"
                onClick={() => {
                  setIsGpReferralModalOpen(false);
                  setCurrentGpReferral({
                    provider: '',
                    gp: false,
                    issue_date: '',
                    period: '12 months',
                    first_visit: '',
                    type: 'General Refer',
                    is_default: false,
                    expired: false,
                    note: ''
                  });
                }}
                className="btn btn-ghost"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setGpReferrals([...gpReferrals, currentGpReferral]);
                  setIsGpReferralModalOpen(false);
                  setCurrentGpReferral({
                    provider: '',
                    gp: false,
                    issue_date: '',
                    period: '12 months',
                    first_visit: '',
                    type: 'General Refer',
                    is_default: false,
                    expired: false,
                    note: ''
                  });
                }}
                className="btn btn-primary"
              >
                Add Referral
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WC/TAC Modal */}
      {isWcTacModalOpen && (
        <div className="modal modal-open">
          <div className="modal-box max-w-2xl">
            <h3 className="font-bold text-lg mb-4">Add WC/TAC Referral</h3>
            
            <div className="space-y-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Date of Injury</span>
                </label>
                <input
                  type="date"
                  value={currentWcTac.date_of_injury}
                  onChange={(e) => setCurrentWcTac({...currentWcTac, date_of_injury: e.target.value})}
                  className="input input-bordered"
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Claim#</span>
                </label>
                <input
                  type="text"
                  value={currentWcTac.claim_number}
                  onChange={(e) => setCurrentWcTac({...currentWcTac, claim_number: e.target.value})}
                  className="input input-bordered"
                  placeholder="123456789"
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Payer</span>
                </label>
                <input
                  type="text"
                  value={currentWcTac.payer}
                  onChange={(e) => setCurrentWcTac({...currentWcTac, payer: e.target.value})}
                  className="input input-bordered"
                  placeholder="Allianz Insurance"
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Employer</span>
                </label>
                <input
                  type="text"
                  value={currentWcTac.employer}
                  onChange={(e) => setCurrentWcTac({...currentWcTac, employer: e.target.value})}
                  className="input input-bordered"
                  placeholder="Global Health"
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Case Manager</span>
                </label>
                <input
                  type="text"
                  value={currentWcTac.case_manager}
                  onChange={(e) => setCurrentWcTac({...currentWcTac, case_manager: e.target.value})}
                  className="input input-bordered"
                  placeholder="Miss Bel Czosnek"
                />
              </div>

              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-2">
                  <input
                    type="checkbox"
                    checked={currentWcTac.is_default}
                    onChange={(e) => setCurrentWcTac({...currentWcTac, is_default: e.target.checked})}
                    className="checkbox checkbox-primary"
                  />
                  <span className="label-text">Set as Default</span>
                </label>
              </div>
            </div>

            <div className="modal-action">
              <button
                type="button"
                onClick={() => {
                  setIsWcTacModalOpen(false);
                  setCurrentWcTac({
                    date_of_injury: '',
                    claim_number: '',
                    payer: '',
                    employer: '',
                    case_manager: '',
                    is_default: false
                  });
                }}
                className="btn btn-ghost"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setWcTacReferrals([...wcTacReferrals, currentWcTac]);
                  setIsWcTacModalOpen(false);
                  setCurrentWcTac({
                    date_of_injury: '',
                    claim_number: '',
                    payer: '',
                    employer: '',
                    case_manager: '',
                    is_default: false
                  });
                }}
                className="btn btn-primary"
              >
                Add Referral
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewPatient;
